/**
 * SubmissionsTable Component
 *
 * Displays form submissions with filtering, status management, and detail view.
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger } from 'nuqs';
import {
  Inbox,
  Check,
  Archive,
  AlertTriangle,
  ChevronDown,
  MoreHorizontal,
  Eye,
  Trash2,
  Monitor,
  Smartphone,
  Globe,
  Loader2,
  RefreshCw,
  Mail,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/client/components/ui/dropdown-menu';
import { useUIStore } from '@/client/stores/ui-store';
import {
  type FormSubmissionResponse,
  type SubmissionStatus,
  SUBMISSION_STATUS_INFO,
  SUBMISSION_STATUSES,
} from '@/shared/schemas/forms';

// =============================================================================
// Types
// =============================================================================

interface SubmissionsTableProps {
  formId: string;
}

const STATUS_ICONS: Record<SubmissionStatus, typeof Inbox> = {
  new: Inbox,
  read: Check,
  archived: Archive,
  spam: AlertTriangle,
};

// =============================================================================
// Main Component
// =============================================================================

export function SubmissionsTable({ formId }: SubmissionsTableProps) {
  const queryClient = useQueryClient();
  const { openSubmissionDetail } = useUIStore();
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'all'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // URL-synced pagination state
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const itemsPerPage = 20;

  // Fetch submissions
  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['form-submissions', formId, statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((page - 1) * itemsPerPage).toString(),
      });
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      const response = await apiFetch(`/api/forms/${formId}/submissions?${params}`);
      if (!response.ok) throw new Error('Failed to fetch submissions');
      return response.json();
    },
  });

  const submissions: FormSubmissionResponse[] = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  // Reset page to 1 when filter changes
  const handleStatusFilterChange = (status: SubmissionStatus | 'all') => {
    setStatusFilter(status);
    setPage(1);
  };

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      submissionId,
      status,
    }: {
      submissionId: string;
      status: SubmissionStatus;
    }) => {
      const response = await apiFetch(`/api/forms/${formId}/submissions/${submissionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error('Failed to update status');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-submissions', formId] });
    },
    onError: () => {
      toast.error('Failed to update status');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (submissionId: string) => {
      const response = await apiFetch(`/api/forms/${formId}/submissions/${submissionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete submission');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Submission deleted');
      queryClient.invalidateQueries({ queryKey: ['form-submissions', formId] });
    },
    onError: () => {
      toast.error('Failed to delete submission');
    },
  });

  // Toggle selection (using functional setState for stable callback reference)
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []); // No dependencies needed!

  const toggleAll = () => {
    if (selectedIds.size === submissionsWithPrimary.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissionsWithPrimary.map((s) => s.id)));
    }
  };

  // Pre-compute primary values for all submissions to avoid O(n) operations in render loop
  const submissionsWithPrimary = useMemo(() => {
    return submissions.map((submission) => {
      // Extract primary value (email > name > first non-empty value)
      const emailField = Object.entries(submission.data).find(([key]) =>
        key.toLowerCase().includes('email')
      );
      if (emailField) {
        return {
          ...submission,
          primaryValue: String(emailField[1]),
          isEmail: true,
        };
      }

      const nameField = Object.entries(submission.data).find(([key]) =>
        key.toLowerCase().includes('name')
      );
      if (nameField) {
        return {
          ...submission,
          primaryValue: String(nameField[1]),
          isEmail: false,
        };
      }

      // First non-empty value
      const firstValue = Object.values(submission.data).find(
        (v) => v !== null && v !== undefined && v !== ''
      );
      return {
        ...submission,
        primaryValue: firstValue ? String(firstValue).substring(0, 50) : 'No data',
        isEmail: false,
      };
    });
  }, [submissions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Status Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                {statusFilter === 'all'
                  ? 'All Submissions'
                  : SUBMISSION_STATUS_INFO[statusFilter].label}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handleStatusFilterChange('all')}>
                All Submissions
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {SUBMISSION_STATUSES.map((status) => {
                const Icon = STATUS_ICONS[status];
                const info = SUBMISSION_STATUS_INFO[status];
                return (
                  <DropdownMenuItem key={status} onClick={() => handleStatusFilterChange(status)}>
                    <Icon className="h-4 w-4 mr-2" />
                    {info.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <span className="text-sm text-muted-foreground">
            {total} submission{total !== 1 ? 's' : ''}
          </span>
        </div>

        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching}>
          <RefreshCw className={cn('h-4 w-4', isRefetching && 'animate-spin')} />
        </Button>
      </div>

      {/* Table */}
      {submissionsWithPrimary.length === 0 ? (
        <div className="text-center py-12 border rounded-xl bg-muted/20">
          <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-1">No submissions yet</p>
          <p className="text-xs text-muted-foreground/70">
            Submissions will appear here once users fill out your form
          </p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === submissionsWithPrimary.length &&
                      submissionsWithPrimary.length > 0
                    }
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Submission
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {submissionsWithPrimary.map((submission) => {
                const StatusIcon = STATUS_ICONS[submission.status];
                const statusInfo = SUBMISSION_STATUS_INFO[submission.status];
                const isSelected = selectedIds.has(submission.id);

                return (
                  <tr
                    key={submission.id}
                    className={cn(
                      'hover:bg-muted/30 transition-colors cursor-pointer',
                      isSelected && 'bg-primary/5'
                    )}
                    onClick={() => openSubmissionDetail(formId, submission.id)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(submission.id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {submission.isEmail ? (
                          <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <div className="h-4 w-4" />
                        )}
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {submission.primaryValue}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                              submission.status === 'new' &&
                                'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                              submission.status === 'read' &&
                                'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
                              submission.status === 'archived' &&
                                'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
                              submission.status === 'spam' &&
                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            )}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.label}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {SUBMISSION_STATUSES.map((status) => {
                            const Icon = STATUS_ICONS[status];
                            const info = SUBMISSION_STATUS_INFO[status];
                            return (
                              <DropdownMenuItem
                                key={status}
                                onClick={() =>
                                  updateStatusMutation.mutate({
                                    submissionId: submission.id,
                                    status,
                                  })
                                }
                                className={cn(status === submission.status && 'bg-muted')}
                              >
                                <Icon className="h-4 w-4 mr-2" />
                                {info.label}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {submission.country && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {submission.country}
                          </span>
                        )}
                        {submission.device && (
                          <span className="flex items-center gap-1">
                            {submission.device === 'mobile' ? (
                              <Smartphone className="h-3 w-3" />
                            ) : (
                              <Monitor className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="text-foreground">
                          {format(new Date(submission.submittedAt), 'MMM d, yyyy')}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {format(new Date(submission.submittedAt), 'h:mm a')}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-muted rounded-lg transition-colors">
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openSubmissionDetail(formId, submission.id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => deleteMutation.mutate(submission.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t bg-muted/20">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">{(page - 1) * itemsPerPage + 1}</span>{' '}
                to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(page * itemsPerPage, total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{total}</span> submissions
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-lg"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="rounded-lg"
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
